import React, { useEffect, useMemo, useReducer, useState, useRef } from 'react';
import { RotateCcw } from 'lucide-react';
import { sans, mono, display, green, greenLight, greenDark, red, amber, useTheme, useCurrency } from '../tokens';
import { boardCeiling2026 } from '../data/boardCeiling2026';
import { deptHierarchy } from '../data/deptHierarchy';
import { MONTHS } from '../data/forecast2026';
import { FY_LIST, CHILD_DEPTS } from '../data/forecast';
import { EXPENSE_CATEGORIES, getAllocation } from '../data/expenseCategories';
import {
  getMonthly, setMonthly, clearMonthly,
  resetAll, overrideCount,
  subscribe,
} from '../data/forecastOverrides';
import { YEARS } from '../data/cashflowForecast';

// ─── Currency helpers ────────────────────────────────────────────────────────
const usdToDisplay = (n, cc) => (cc?.currency === 'USD' || !cc) ? n : (n || 0) * (cc.fxRate || 1);
const displayToUsd = (v, cc) => (cc?.currency === 'USD' || !cc) ? v : (v || 0) / (cc.fxRate || 1);

const fmtNum = (v) => {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${Math.round(v / 1_000).toLocaleString()}K`;
  return `$${Math.round(v).toLocaleString()}`;
};
const fmt = (n, cc) => fmtNum(usdToDisplay(n, cc));
const fmtPct = (v) => v == null || !isFinite(v) ? '—' : `${v.toFixed(1)}%`;

// Human input parser — accepts 1.5M, 250k, 1,234,567
function parseAmount(input) {
  if (!input) return null;
  const s = String(input).trim().replace(/[$,\s]/g, '').toLowerCase();
  if (!s) return null;
  let mult = 1;
  let raw = s;
  if (s.endsWith('m')) { mult = 1_000_000; raw = s.slice(0, -1); }
  else if (s.endsWith('k')) { mult = 1_000; raw = s.slice(0, -1); }
  const n = parseFloat(raw);
  if (isNaN(n)) return null;
  return n * mult;
}

function useOverridesSubscription() {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => subscribe(force), []);
  return force;
}

// ─── Editable Cell ───────────────────────────────────────────────────────────
function EditableCell({ value, isOverride, onSave, onReset, currencyCtx, t, cellStyle }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = () => {
    if (value == null) return;
    const dv = usdToDisplay(value, currencyCtx);
    setDraft(dv == null ? '' : Math.round(dv).toLocaleString());
    setEditing(true);
  };

  const commit = () => {
    const parsed = parseAmount(draft);
    if (parsed != null) onSave(displayToUsd(parsed, currencyCtx));
    setEditing(false);
  };

  if (editing) {
    return (
      <td style={{ ...cellStyle, padding: 4, background: greenLight }}>
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setEditing(false);
          }}
          onBlur={commit}
          style={{
            width: '100%', minWidth: 0, fontFamily: sans, fontSize: 12,
            padding: '4px 8px', textAlign: 'right',
            border: `1.5px solid ${green}`, borderRadius: 4,
            outline: 'none', background: '#fff', color: t.textPrimary,
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 500,
          }}
        />
      </td>
    );
  }

  return (
    <td
      onDoubleClick={startEdit}
      title={isOverride ? 'Edited · double-click to change' : 'Double-click to edit'}
      style={{
        ...cellStyle,
        cursor: 'pointer',
        background: isOverride ? `${green}10` : (cellStyle.background || 'transparent'),
        position: 'relative',
        userSelect: 'none',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
        {fmt(value, currencyCtx)}
        {isOverride && (
          <button
            onClick={(e) => { e.stopPropagation(); onReset(); }}
            title="Reset"
            style={{
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              color: green, display: 'inline-flex', borderRadius: 3, marginLeft: 4,
            }}
          >
            <RotateCcw size={9} />
          </button>
        )}
      </span>
    </td>
  );
}

// ─── Build view rows + columns ───────────────────────────────────────────────
function buildView({ groupBy, granularity, year }) {
  // Columns
  let columns;
  if (granularity === 'annual') {
    // Annual = multi-year compare — one column per year
    columns = FY_LIST.map((y) => ({ key: `y${y}`, label: `FY${y}`, multiYearForYear: y }));
  } else if (granularity === 'monthly') {
    columns = MONTHS.map((m, i) => ({ key: `m${i}`, label: m, monthIdx: i }));
  } else {
    columns = ['Q1', 'Q2', 'Q3', 'Q4'].map((label, qi) => ({ key: `q${qi}`, label, quarter: qi }));
  }

  // Groups (each with: header row + account rows + subtotal row)
  const sections = [];

  if (groupBy === 'department') {
    boardCeiling2026.forEach((parent) => {
      const children = deptHierarchy[parent.id] || [];
      const accountRows = children.map((child) => ({
        kind: 'account',
        id: `child:${child}`,
        label: child,
        childDept: child,
        parentId: parent.id,
      }));
      sections.push({
        id: parent.id,
        groupLabel: parent.parentDept,
        owner: parent.owner,
        accent: '#1FAE7C',
        accountRows,
        // Allow direct edit on parent (annual) when granularity is annual and parent has 0 or 1 children
        parentRow: { kind: 'parentTotal', id: `parent:${parent.id}`, parentId: parent.id, label: parent.parentDept, owner: parent.owner },
      });
    });
  } else if (groupBy === 'expense-type') {
    // Each category is a section. Click to expand → see GL accounts.
    // GL value = category total × within-category share. Math ties.
    EXPENSE_CATEGORIES.forEach((cat) => {
      sections.push({
        id: cat.id,
        groupLabel: cat.label,
        accent: cat.accent,
        categoryId: cat.id,
        // Section's "summary" value comes from categoryMonthly().
        summaryKind: 'category',
        // Account rows = GL codes within this category.
        accountRows: cat.accounts.map((a) => ({
          kind: 'glAccount',
          id: `gl:${cat.id}:${a.code}`,
          label: a.name,
          glCode: a.code,
          glName: a.name,
          categoryId: cat.id,
          accountShare: a.share,
        })),
      });
    });
  }

  return { columns, sections };
}

// Sum across all depts: dept_monthly × dept_allocation[categoryId] for given (year, month)
function categoryMonthly(categoryId, year, monthIdx) {
  return CHILD_DEPTS.reduce((sum, dept) => {
    const v = getMonthly(dept, year, monthIdx) || 0;
    return sum + v * getAllocation(dept, categoryId);
  }, 0);
}

// ─── Compute one cell's value (USD source) ───────────────────────────────────
function computeCell({ row, col, year, granularity }) {
  // Multi-year compare: each col targets a specific year, value = annual total for that year
  if (col.multiYearForYear != null) {
    const yr = col.multiYearForYear;
    if (row.kind === 'account') {
      let t = 0;
      for (let m = 0; m < 12; m++) t += getMonthly(row.childDept, yr, m) || 0;
      return t;
    }
    if (row.kind === 'category') {
      let t = 0;
      for (let m = 0; m < 12; m++) t += categoryMonthly(row.categoryId, yr, m);
      return t;
    }
    if (row.kind === 'glAccount') {
      let t = 0;
      for (let m = 0; m < 12; m++) t += categoryMonthly(row.categoryId, yr, m);
      return t * row.accountShare;
    }
    return null;
  }

  if (row.kind === 'account') {
    if (granularity === 'monthly') return getMonthly(row.childDept, year, col.monthIdx);
    if (granularity === 'quarterly') {
      let total = 0;
      for (let m = col.quarter * 3; m < col.quarter * 3 + 3; m++) total += getMonthly(row.childDept, year, m) || 0;
      return total;
    }
    let total = 0;
    for (let m = 0; m < 12; m++) total += getMonthly(row.childDept, year, m) || 0;
    return total;
  }

  if (row.kind === 'category') {
    // Derived from dept × allocation. Math ties to dept total by construction.
    if (granularity === 'monthly') return categoryMonthly(row.categoryId, year, col.monthIdx);
    if (granularity === 'quarterly') {
      let total = 0;
      for (let m = col.quarter * 3; m < col.quarter * 3 + 3; m++) total += categoryMonthly(row.categoryId, year, m);
      return total;
    }
    let total = 0;
    for (let m = 0; m < 12; m++) total += categoryMonthly(row.categoryId, year, m);
    return total;
  }

  if (row.kind === 'glAccount') {
    // GL = (category total) × (within-category share). Sums to category total exactly.
    const catGetter = (m) => categoryMonthly(row.categoryId, year, m);
    if (granularity === 'monthly') return catGetter(col.monthIdx) * row.accountShare;
    if (granularity === 'quarterly') {
      let total = 0;
      for (let m = col.quarter * 3; m < col.quarter * 3 + 3; m++) total += catGetter(m);
      return total * row.accountShare;
    }
    let total = 0;
    for (let m = 0; m < 12; m++) total += catGetter(m);
    return total * row.accountShare;
  }

  return null;
}

function isOverride({ row, col, year, granularity }) {
  if (granularity === 'monthly' && row.kind === 'account') {
    try {
      const cache = JSON.parse(localStorage.getItem('finkloz.forecast.overrides') || '{}');
      return `m:${row.childDept}:${year}-${col.monthIdx}` in cache;
    } catch { return false; }
  }
  return false;
}

function isEditable({ row, granularity, groupBy }) {
  // Only child dept × month cells are editable. Parent and category rows are
  // computed from children — no separate override path. Math always ties.
  if (groupBy !== 'department') return false;
  if (granularity === 'monthly' && row.kind === 'account') return true;
  return false;
}

function saveCell({ row, col, year, granularity, newValue }) {
  if (granularity === 'monthly' && row.kind === 'account') {
    setMonthly(row.childDept, year, col.monthIdx, newValue);
  }
}

function resetCell({ row, col, year, granularity }) {
  if (granularity === 'monthly' && row.kind === 'account') {
    clearMonthly(row.childDept, year, col.monthIdx);
  }
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function AnnualPLForecast() {
  const t = useTheme();
  const cc = useCurrency();
  useOverridesSubscription();

  const [groupBy, setGroupBy] = useState('department');
  const [granularity, setGranularity] = useState('quarterly');
  const [year, setYear] = useState(2026);
  const [expandedSections, setExpandedSections] = useState(new Set());

  const isExpanded = (id) => expandedSections.has(id);
  const toggleSection = (id) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const { columns, sections } = useMemo(
    () => buildView({ groupBy, granularity, year }),
    [groupBy, granularity, year]
  );

  // Both views now support all years 2026-2031 — no snap needed.

  // Annual totals per column (across all groups)
  const grandTotal = useMemo(() => {
    return columns.map((col) => {
      let total = 0;
      sections.forEach((sect) => {
        sect.accountRows.forEach((row) => {
          const v = computeCell({ row, col, year, granularity });
          if (v != null) total += v;
        });
      });
      return total;
    });
  }, [columns, sections, year, granularity]);

  const grandTotalAnnual = grandTotal.reduce((s, v) => s + v, 0);

  // Section subtotals (per group, per column, plus annual)
  const sectionTotals = useMemo(() => {
    return sections.map((sect) => {
      const perCol = columns.map((col) => {
        return sect.accountRows.reduce((s, row) => s + (computeCell({ row, col, year, granularity }) || 0), 0);
      });
      // Annual = sum of all 12 months for each row (or sum across all years in annual mode)
      const yearsToSum = granularity === 'annual' ? FY_LIST : [year];
      const annual = sect.accountRows.reduce((sum, row) => {
        let yr = 0;
        yearsToSum.forEach((y2) => {
          for (let m = 0; m < 12; m++) {
            if (row.kind === 'account') yr += getMonthly(row.childDept, y2, m) || 0;
            else if (row.kind === 'category') yr += categoryMonthly(row.categoryId, y2, m);
            else if (row.kind === 'glAccount') yr += categoryMonthly(row.categoryId, y2, m) * row.accountShare;
          }
        });
        return sum + yr;
      }, 0);
      return { perCol, annual };
    });
  }, [sections, columns, year, granularity]);

  const overrideTotal = overrideCount();

  // Cell base style — matches CashflowForecast table for visual alignment
  const cellStyleBase = {
    padding: '8px 14px',
    fontFamily: mono,
    fontSize: 12,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    color: t.textPrimary,
    fontWeight: 500,
  };

  return (
    <div style={{
      background: t.bgCard,
      border: `1px solid rgba(15, 42, 36, 0.06)`,
      borderRadius: 14,
      padding: '24px 28px 20px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{
            fontSize: 11, fontWeight: 600, color: t.textMuted,
            letterSpacing: 0.8, textTransform: 'uppercase', fontFamily: sans, marginBottom: 6,
          }}>
            P&L Forecast
          </div>
          <div style={{
            fontSize: 22, fontWeight: 600, fontFamily: display,
            color: t.textPrimary, letterSpacing: -0.3, lineHeight: 1.15,
          }}>
            {granularity === 'annual' ? `${FY_LIST[0]}–${FY_LIST[FY_LIST.length-1]}` : `FY${year}`} ·{' '}
            <span style={{ fontVariantNumeric: 'tabular-nums', fontFamily: mono, fontWeight: 600 }}>
              {fmt(grandTotalAnnual, cc)}
            </span>
            <span style={{ color: t.textMuted, fontWeight: 500 }}>
              {' '}· {groupBy === 'department' ? 'by department' : 'by expense category'}
            </span>
          </div>
        </div>
        {overrideTotal > 0 && (
          <button
            onClick={() => { if (window.confirm(`Reset all ${overrideTotal} edits?`)) resetAll(); }}
            style={{
              padding: '6px 12px', borderRadius: 6, border: `1px solid ${amber}`,
              background: 'transparent', color: '#8D5A0B',
              fontFamily: sans, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <RotateCcw size={12} /> Reset {overrideTotal} edit{overrideTotal === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <SegmentToggle
          options={[
            { key: 'department', label: 'By department' },
            { key: 'expense-type', label: 'By expense type' },
          ]}
          value={groupBy} onChange={setGroupBy} t={t}
        />
        <SegmentToggle
          options={[
            { key: 'monthly', label: 'Monthly' },
            { key: 'quarterly', label: 'Quarterly' },
            { key: 'annual', label: 'Annual' },
          ]}
          value={granularity} onChange={setGranularity} t={t}
        />
        {granularity !== 'annual' && (
          <div style={{ display: 'flex', gap: 4 }}>
            {YEARS.map((y) => {
              const active = y === year;
              return (
                <button
                  key={y}
                  onClick={() => setYear(y)}
                  style={{
                    padding: '5px 12px', borderRadius: 6,
                    border: `1px solid ${active ? green : t.border}`,
                    background: active ? greenLight : 'transparent',
                    color: active ? greenDark : t.textSecondary,
                    fontFamily: sans, fontSize: 11,
                    fontWeight: active ? 600 : 500,
                    cursor: 'pointer',
                  }}
                >
                  {y}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Hint */}
      <div style={{ fontSize: 11, color: t.textFaint, fontFamily: sans, marginBottom: 12 }}>
        {granularity === 'annual'
          ? `Comparing FY${FY_LIST[0]}–FY${FY_LIST[FY_LIST.length-1]} side by side · click any group with multiple lines to expand`
          : granularity === 'monthly'
          ? 'Double-click any monthly cell to edit · accepts 1.5M, 250k, raw numbers · click any group with multiple lines to expand'
          : 'Click any group with multiple lines to expand · switch to Monthly to edit values'}
      </div>

      {/* Pivot table */}
      <div style={{ overflowX: 'auto', border: `1px solid ${t.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: granularity === 'monthly' ? 1100 : 720 }}>
          <thead>
            <tr style={{ background: t.bgSurface, borderBottom: `1px solid ${t.border}` }}>
              <th style={{
                textAlign: 'left', padding: '12px 16px',
                fontSize: 10, fontWeight: 600, color: t.textMuted,
                textTransform: 'uppercase', letterSpacing: 0.6,
                position: 'sticky', left: 0, background: t.bgSurface, zIndex: 2,
                minWidth: groupBy === 'expense-type' ? 280 : 220,
              }}>
                {groupBy === 'department' ? 'Department' : 'GL Account'}
              </th>
              {columns.map((col) => (
                <th key={col.key} style={{
                  textAlign: 'right', padding: '12px 14px',
                  fontSize: 10, fontWeight: 600, color: t.textMuted,
                  textTransform: 'uppercase', letterSpacing: 0.6,
                  fontFamily: sans,
                  minWidth: granularity === 'monthly' ? 78 : 96,
                }}>
                  {col.label}
                </th>
              ))}
              {/* FY total + Share columns. In annual mode this shows total across all years. */}
              <th style={{
                textAlign: 'right', padding: '12px 14px',
                fontSize: 10, fontWeight: 700, color: t.textPrimary,
                textTransform: 'uppercase', letterSpacing: 0.6,
                fontFamily: sans,
                background: t.bgCard,
                minWidth: 96,
              }}>
                {granularity === 'annual' ? '6yr Total' : `FY${year}`}
              </th>
              <th style={{
                textAlign: 'right', padding: '12px 14px',
                fontSize: 10, fontWeight: 600, color: t.textMuted,
                textTransform: 'uppercase', letterSpacing: 0.6,
                fontFamily: sans, minWidth: 70,
              }}>
                Share
              </th>
            </tr>
          </thead>
          <tbody>
            {sections.map((sect, sectIdx) => {
              const sectAnnual = sectionTotals[sectIdx].annual;
              const sectShare = grandTotalAnnual > 0 ? (sectAnnual / grandTotalAnnual) * 100 : 0;
              const isSingleRow = sect.accountRows.length === 1;
              const isEmptySection = sect.accountRows.length === 0;
              const expanded = isExpanded(sect.id);

              if (isEmptySection) return null;

              // SINGLE-LINE SECTION — render as one flat row, no header/expand needed
              if (isSingleRow) {
                const row = sect.accountRows[0];
                return (
                  <tr key={sect.id} style={{
                    borderBottom: `1px solid ${t.border}`,
                  }}>
                    <td style={{
                      padding: '11px 16px',
                      fontFamily: sans, fontSize: 13, fontWeight: 500,
                      color: t.textPrimary,
                      position: 'sticky', left: 0, background: t.bgCard, zIndex: 1,
                    }}>
                      {sect.groupLabel}
                    </td>
                    {columns.map((col) => {
                      const value = computeCell({ row, col, year, granularity });
                      const editable = isEditable({ row, granularity, groupBy });
                      const ovr = isOverride({ row, col, year, granularity });
                      if (editable) {
                        return (
                          <EditableCell
                            key={col.key}
                            value={value}
                            isOverride={ovr}
                            onSave={(nv) => saveCell({ row, col, year, granularity, newValue: nv })}
                            onReset={() => resetCell({ row, col, year, granularity })}
                            currencyCtx={cc}
                            t={t}
                            cellStyle={cellStyleBase}
                          />
                        );
                      }
                      return (
                        <td key={col.key} style={cellStyleBase}>
                          {fmt(value, cc)}
                        </td>
                      );
                    })}
                    <td style={{
                      ...cellStyleBase,
                      background: t.bgSurface,
                      fontWeight: 600,
                    }}>
                      {fmt(sectAnnual, cc)}
                    </td>
                    <td style={{
                      padding: '11px 14px', textAlign: 'right',
                      fontFamily: mono, fontSize: 11, fontWeight: 500,
                      color: t.textMuted,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {fmtPct(sectShare)}
                    </td>
                  </tr>
                );
              }

              // MULTI-LINE SECTION — expandable subtotal header
              return (
                <React.Fragment key={sect.id}>
                  <tr
                    onClick={() => toggleSection(sect.id)}
                    style={{
                      background: expanded ? '#F4F1EB' : t.bgCard,
                      borderTop: expanded ? `1px solid rgba(15,42,36,0.1)` : 'none',
                      borderBottom: `1px solid ${t.border}`,
                      cursor: 'pointer',
                      transition: 'background 120ms ease',
                    }}
                  >
                    <td style={{
                      padding: '11px 16px',
                      fontFamily: sans, fontSize: 13, fontWeight: 600,
                      color: t.textPrimary,
                      position: 'sticky', left: 0,
                      background: expanded ? '#F4F1EB' : t.bgCard, zIndex: 1,
                      userSelect: 'none',
                    }}>
                      <span style={{
                        display: 'inline-block', width: 14, color: t.textMuted, fontSize: 9, marginRight: 8,
                      }}>
                        {expanded ? '▼' : '▶'}
                      </span>
                      {sect.groupLabel}
                    </td>
                    {columns.map((col, ci) => (
                      <td key={col.key} style={{
                        ...cellStyleBase, fontWeight: 600,
                        background: expanded ? '#F4F1EB' : t.bgCard,
                      }}>
                        {fmt(sectionTotals[sectIdx].perCol[ci], cc)}
                      </td>
                    ))}
                    <td style={{
                      ...cellStyleBase, fontWeight: 700,
                      background: expanded ? '#F4F1EB' : t.bgSurface,
                    }}>
                      {fmt(sectAnnual, cc)}
                    </td>
                    <td style={{
                      padding: '11px 14px', textAlign: 'right',
                      fontFamily: mono, fontSize: 11, fontWeight: 600,
                      color: t.textMuted,
                      fontVariantNumeric: 'tabular-nums',
                      background: expanded ? '#F4F1EB' : t.bgCard,
                    }}>
                      {fmtPct(sectShare)}
                    </td>
                  </tr>

                  {/* DETAIL ROWS — only when expanded */}
                  {expanded && sect.accountRows.map((row) => {
                    let yrTotal = 0;
                    const yrsToSum = granularity === 'annual' ? FY_LIST : [year];
                    yrsToSum.forEach((y2) => {
                      for (let m = 0; m < 12; m++) {
                        if (row.kind === 'account') yrTotal += getMonthly(row.childDept, y2, m) || 0;
                        else if (row.kind === 'category') yrTotal += categoryMonthly(row.categoryId, y2, m);
                        else if (row.kind === 'glAccount') yrTotal += categoryMonthly(row.categoryId, y2, m) * row.accountShare;
                      }
                    });
                    const rowShare = grandTotalAnnual > 0 ? (yrTotal / grandTotalAnnual) * 100 : 0;

                    return (
                      <tr key={row.id} style={{
                        borderBottom: '1px solid rgba(15,42,36,0.04)',
                      }}>
                        <td style={{
                          padding: '7px 16px 7px 40px',
                          fontFamily: sans, fontSize: 12,
                          color: t.textSecondary,
                          position: 'sticky', left: 0, background: t.bgCard, zIndex: 1,
                        }}>
                          {row.glCode && (
                            <span style={{ fontFamily: mono, fontSize: 10, color: t.textMuted, marginRight: 8 }}>
                              {row.glCode} ·
                            </span>
                          )}
                          {row.label}
                        </td>
                        {columns.map((col) => {
                          const value = computeCell({ row, col, year, granularity });
                          const editable = isEditable({ row, granularity, groupBy });
                          const ovr = isOverride({ row, col, year, granularity });
                          if (editable) {
                            return (
                              <EditableCell
                                key={col.key}
                                value={value}
                                isOverride={ovr}
                                onSave={(nv) => saveCell({ row, col, year, granularity, newValue: nv })}
                                onReset={() => resetCell({ row, col, year, granularity })}
                                currencyCtx={cc}
                                t={t}
                                cellStyle={cellStyleBase}
                              />
                            );
                          }
                          return (
                            <td key={col.key} style={{
                              ...cellStyleBase,
                              color: value == null ? t.textFaint : t.textSecondary,
                            }}>
                              {fmt(value, cc)}
                            </td>
                          );
                        })}
                        <td style={{
                          ...cellStyleBase,
                          background: t.bgSurface,
                          fontWeight: 600,
                          color: yrTotal === 0 ? t.textFaint : t.textPrimary,
                        }}>
                          {fmt(yrTotal, cc)}
                        </td>
                        <td style={{
                          padding: '7px 14px', textAlign: 'right',
                          fontFamily: mono, fontSize: 11, color: t.textMuted,
                          fontVariantNumeric: 'tabular-nums', fontWeight: 500,
                        }}>
                          {rowShare > 0 ? fmtPct(rowShare) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* GRAND TOTAL */}
            <tr style={{
              background: '#E8F4EE',
              borderTop: `2px solid ${green}`,
            }}>
              <td style={{
                padding: '14px 16px',
                fontFamily: sans, fontSize: 13, fontWeight: 700,
                color: '#0F6E56',
                position: 'sticky', left: 0, background: '#E8F4EE', zIndex: 1,
              }}>
                Total all expenses
              </td>
              {grandTotal.map((v, i) => (
                <td key={i} style={{
                  ...cellStyleBase, fontWeight: 700, background: '#E8F4EE',
                  fontSize: 13, color: '#0F6E56', padding: '14px 14px',
                }}>
                  {fmt(v, cc)}
                </td>
              ))}
              <td style={{
                ...cellStyleBase, fontWeight: 700, background: '#E8F4EE',
                fontSize: 13, color: '#0F6E56', padding: '14px 14px',
              }}>
                {fmt(grandTotalAnnual, cc)}
              </td>
              <td style={{
                padding: '14px 14px', textAlign: 'right',
                fontFamily: mono, fontSize: 13, fontWeight: 700,
                color: '#0F6E56', background: '#E8F4EE',
                fontVariantNumeric: 'tabular-nums',
              }}>
                100%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, fontSize: 10, color: t.textFaint, fontFamily: sans, lineHeight: 1.6 }}>
        Edits flow through to the cashflow chart and Board ceiling dashboard. Source values in USD{cc?.currency === 'CAD' ? ` · displayed in CAD @ ${cc.fxRate?.toFixed(4)}` : ''}.
        {' '}Source: full child-dept × month forecast for 2026–2031 from the forecast PDF.
        {' '}P&L grand total ≡ Operating Burn in cashflow ≡ direct sum of dept × month. Math ties trivially.
      </div>
    </div>
  );
}

// ─── Segmented control ───────────────────────────────────────────────────────
function SegmentToggle({ options, value, onChange, t }) {
  return (
    <div style={{
      display: 'inline-flex', borderRadius: 999,
      background: t.bgSurface, padding: 2,
      border: `1px solid ${t.border}`,
    }}>
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            onClick={() => onChange(opt.key)}
            style={{
              padding: '5px 14px', borderRadius: 999, border: 'none',
              background: active ? t.bgCard : 'transparent',
              color: active ? t.textPrimary : t.textMuted,
              fontFamily: sans, fontSize: 11,
              fontWeight: active ? 600 : 500,
              cursor: 'pointer',
              boxShadow: active ? '0 1px 2px rgba(15,42,36,0.06)' : 'none',
              transition: 'all 120ms ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
